import React from 'react';
import { createJBrowseTheme } from '@jbrowse/core/ui';
import { observer } from 'mobx-react';
import { bpSpanPx, revcom, complement, defaultStarts, defaultStops, defaultCodonTable, generateCodonTable, } from '@jbrowse/core/util';
function Translation({ codonTable, seq, frame, bpPerPx, colorByCDS, region, seqStart, height, y, reverse = false, theme, }) {
    var _a, _b;
    const normalizedFrame = Math.abs(frame) - 1;
    const seqFrame = seqStart % 3;
    const frameShift = (normalizedFrame - seqFrame + 3) % 3;
    const frameShiftAdjustedSeqLength = seq.length - frameShift;
    const multipleOfThreeLength = frameShiftAdjustedSeqLength - (frameShiftAdjustedSeqLength % 3);
    const seqSliced = seq.slice(frameShift, frameShift + multipleOfThreeLength);
    const translated = [];
    for (let i = 0; i < seqSliced.length; i += 3) {
        const codon = seqSliced.slice(i, i + 3);
        const normalizedCodon = reverse ? revcom(codon) : codon;
        const aminoAcid = codonTable[normalizedCodon] || '';
        translated.push({
            letter: aminoAcid,
            codon: normalizedCodon.toUpperCase(),
        });
    }
    const width = (region.end - region.start) / bpPerPx;
    const codonWidth = (1 / bpPerPx) * 3;
    const renderLetter = 1 / bpPerPx >= 12;
    const frameOffset = frameShift / bpPerPx;
    const startOffset = (region.start - seqStart) / bpPerPx;
    const offset = frameOffset - startOffset;
    const defaultFill = colorByCDS
        ? (_a = theme === null || theme === void 0 ? void 0 : theme.palette.framesCDS.at(frame)) === null || _a === void 0 ? void 0 : _a.main
        : (_b = theme === null || theme === void 0 ? void 0 : theme.palette.frames.at(frame)) === null || _b === void 0 ? void 0 : _b.main;
    return (React.createElement(React.Fragment, null,
        React.createElement("rect", { x: 0, y: y, width: width, height: height, fill: defaultFill }),
        translated.map((element, index) => {
            const x = region.reversed
                ? width - (index + 1) * codonWidth - offset
                : codonWidth * index + offset;
            const { letter, codon } = element;
            const codonFill = defaultStarts.includes(codon)
                ? theme === null || theme === void 0 ? void 0 : theme.palette.startCodon
                : defaultStops.includes(codon)
                    ? theme === null || theme === void 0 ? void 0 : theme.palette.stopCodon
                    : undefined;
            if (!(renderLetter || codonFill)) {
                return null;
            }
            return (React.createElement(React.Fragment, { key: `${index}-${letter}` },
                React.createElement("rect", { x: x, y: y, width: renderLetter
                        ? codonWidth
                        : codonWidth + 0.7 /* small fudge factor when zoomed out*/, height: height, stroke: renderLetter ? '#555' : 'none', fill: codonFill || 'none' }),
                renderLetter ? (React.createElement("text", { x: x + codonWidth / 2, fontSize: height - 2, y: y + height / 2, dominantBaseline: "middle", textAnchor: "middle" }, letter)) : null));
        })));
}
function DNA(props) {
    const { bpPerPx, region, feature, theme, height, seq, y } = props;
    const render = 1 / bpPerPx >= 12;
    const [leftPx, rightPx] = bpSpanPx(feature.get('start'), feature.get('end'), region, bpPerPx);
    const reverse = region.reversed;
    const len = feature.get('end') - feature.get('start');
    const w = Math.max((rightPx - leftPx) / len, 0.8);
    return (React.createElement(React.Fragment, null, seq.split('').map((letter, index) => {
        // @ts-expect-error
        const color = theme.palette.bases[letter.toUpperCase()];
        const x = reverse ? rightPx - (index + 1) * w : leftPx + index * w;
        return (React.createElement(React.Fragment, { key: index },
            React.createElement("rect", { x: x, y: y, width: w, height: height, fill: color ? color.main : '#aaa', stroke: render ? '#555' : 'none' }),
            render ? (React.createElement("text", { x: x + w / 2, y: y + height / 2, dominantBaseline: "middle", textAnchor: "middle", fontSize: height - 2, fill: color ? theme.palette.getContrastText(color.main) : 'black' }, letter)) : null));
    })));
}
function SequenceSVG({ regions, theme: configTheme, colorByCDS, features = new Map(), showReverse = true, showForward = true, showTranslation = true, bpPerPx, rowHeight, }) {
    const [region] = regions;
    const theme = createJBrowseTheme(configTheme);
    const codonTable = generateCodonTable(defaultCodonTable);
    const [feature] = [...features.values()];
    if (!feature) {
        return null;
    }
    const seq = feature.get('seq');
    if (!seq) {
        return null;
    }
    // incrementer for the y-position of the current sequence being rendered
    // (applies to both translation rows and dna rows)
    let currY = -rowHeight;
    const showDNA = bpPerPx <= 1;
    const forwardFrames = showTranslation && showForward ? [3, 2, 1] : [];
    const reverseFrames = showTranslation && showReverse ? [-1, -2, -3] : [];
    // if region.reversed, the forward translation is on bottom, reverse on top
    const [topFrames, bottomFrames] = region.reversed
        ? [reverseFrames.toReversed(), forwardFrames.toReversed()]
        : [forwardFrames, reverseFrames];
    return (React.createElement(React.Fragment, null,
        topFrames.map(index => (React.createElement(Translation, { key: `translation-${index}`, colorByCDS: colorByCDS, seq: seq, y: (currY += rowHeight), codonTable: codonTable, frame: index, bpPerPx: bpPerPx, region: region, seqStart: feature.get('start'), theme: theme, height: rowHeight, reverse: region.reversed }))),
        showForward && showDNA ? (React.createElement(DNA, { height: rowHeight, y: (currY += rowHeight), feature: feature, region: region, seq: region.reversed ? complement(seq) : seq, bpPerPx: bpPerPx, theme: theme })) : null,
        showReverse && showDNA ? (React.createElement(DNA, { height: rowHeight, y: (currY += rowHeight), feature: feature, region: region, seq: region.reversed ? seq : complement(seq), bpPerPx: bpPerPx, theme: theme })) : null,
        bottomFrames.map(index => (React.createElement(Translation, { key: `rev-translation-${index}`, colorByCDS: colorByCDS, seq: seq, y: (currY += rowHeight), codonTable: codonTable, frame: index, bpPerPx: bpPerPx, region: region, seqStart: feature.get('start'), theme: theme, height: rowHeight, reverse: !region.reversed })))));
}
function Wrapper({ exportSVG, width, totalHeight, children, }) {
    return exportSVG ? (children) : (React.createElement("svg", { "data-testid": "sequence_track", width: width, height: totalHeight, style: {
            // use block because svg by default is inline, which adds a margin
            display: 'block',
            width,
            height: totalHeight,
            userSelect: 'none',
        } }, children));
}
const DivSequenceRendering = observer(function (props) {
    const { regions, bpPerPx, sequenceHeight } = props;
    const [region] = regions;
    const width = (region.end - region.start) / bpPerPx;
    return (React.createElement(Wrapper, { ...props, totalHeight: sequenceHeight, width: width },
        React.createElement(SequenceSVG, { ...props })));
});
export default DivSequenceRendering;
